import EmployeesIcon from '@assets/icons/dashed-circled-user.svg';
import i18n from '@/utils/i18n';
import NoInternetIcon from '@assets/icons/no-internet.svg';
import React, { useCallback, useState } from 'react';
import { AppPageLayout } from '@experiences/templates/app-page-layout';
import { Button, IconButton, Icons, SidePanel } from '@usepepper/sprout-kit-ui';
import { ContentWithPanelTemplate } from '@experiences/templates/content-with-panel-template';
import { createStyles } from '@/contexts/theme-context';
import { EmployeeEditPanelContent } from '@/experiences/sales-portal/domain/accounts/screens/employee-list/containers/employee-edit-panel/employee-edit-panel-content';
import { EmployeeInvitePanelContent } from '@/experiences/sales-portal/domain/accounts/screens/employee-list/containers/employee-invite-drawer/employee-invite-panel-content';
import { EmployeeListItem } from '@sales-portal/domain/accounts/screens/employee-list/components/employee-list-item';
import { EmployeeListSkeleton } from '@sales-portal/domain/accounts/screens/employee-list/components/employee-list-skeleton';
import { EmployeeSummary } from '@sales-portal/domain/accounts/screens/employee-list/components/employee-summary';
import { EmptyState } from '@experiences/components/empty-state';
import { ErrorState } from '@sales-portal/components/error-state';
import { getEmployeeSummary } from '@sales-portal/domain/accounts/screens/employee-list/blocs/utils/employee-summary';
import { RestaurantEmployee } from '@sales-portal/domain/accounts/screens/employee-list/types';
import { SALES_PORTAL_ROUTES } from '@/navigators/routes';
import { SalesHubShellTemplate } from '@/experiences/sales-portal/templates/sales-hub-shell-template';
import { SalesPortalStackParams } from '@/navigators/types';
import { SearchInput } from '@usepepper/ui/atoms/search-input';
import { StackScreenProps } from '@react-navigation/stack';
import { Typography } from '@usepepper/ui/atoms/typography';
import { useEmployeeNotificationsLoader } from '@/experiences/sales-portal/domain/accounts/screens/employee-list/blocs/loaders/use-employee-notifications-loader';
import { useFilteredEmployees } from '@sales-portal/domain/accounts/screens/employee-list/blocs/hooks/use-filtered-employees';
import { useRemoveEmployeeMutator } from '@sales-portal/domain/accounts/screens/employee-list/blocs/mutators/use-remove-employee-mutator';
import { useRestaurantEmployeesLoader } from '@sales-portal/domain/accounts/screens/employee-list/blocs/loaders/use-restaurant-employees-loader';
import { View } from 'react-native';

const EMPTY_STATE_ICON_SIZE_PX = 48;

type Props = StackScreenProps<
  SalesPortalStackParams,
  | typeof SALES_PORTAL_ROUTES.EmployeeList
  | typeof SALES_PORTAL_ROUTES.ProspectEmployeeList
>;

type PanelState =
  | { employee: RestaurantEmployee; kind: 'edit' }
  | { kind: 'invite' };

export const EmployeeListScreen: React.FC<Props> = ({ route }) => {
  const { restaurantUUID, supplierUUID } = route.params;
  const [styles, theme] = useStyles();

  const pageTitle =
    route.name === SALES_PORTAL_ROUTES.ProspectEmployeeList
      ? i18n.t('sales_portal.sidebar.prospects')
      : i18n.t('sales_portal.account.title');

  const [panelState, setPanelState] = useState<PanelState | null>(null);

  const { employees, error, loading, refetch } = useRestaurantEmployeesLoader({
    restaurantUUID,
    supplierUUID,
  });

  const { filteredEmployees, searchText, setSearchText } =
    useFilteredEmployees(employees);

  const selectedEmployee =
    panelState?.kind === 'edit' ? panelState.employee : null;

  const {
    notifications,
    error: notificationsError,
    refetch: refetchNotifications,
  } = useEmployeeNotificationsLoader({
    employeeUUID: selectedEmployee?.employee_id,
  });

  const handlePanelClose = () => {
    setPanelState(null);
  };

  const handleEmployeeRemoved = useCallback((removedEmployeeId: string) => {
    setPanelState((current) =>
      current?.kind === 'edit' &&
      current.employee.employee_id === removedEmployeeId
        ? null
        : current,
    );
  }, []);

  const { onRemovePress, removing } = useRemoveEmployeeMutator({
    employee: selectedEmployee,
    onRemoved: handleEmployeeRemoved,
    restaurantUUID,
    supplierUUID,
  });

  const openInvitePanel = () => {
    setPanelState({ kind: 'invite' });
  };

  const openEditPanel = (employee: RestaurantEmployee) => {
    setPanelState({ employee, kind: 'edit' });
  };

  const emptyStateIcon = (
    <EmployeesIcon
      height={EMPTY_STATE_ICON_SIZE_PX}
      stroke={theme.sk.color.icon.primary}
      width={EMPTY_STATE_ICON_SIZE_PX}
    />
  );

  const renderBody = () => {
    if (loading && employees.length === 0) {
      return <EmployeeListSkeleton />;
    }

    if (error) {
      return (
        <ErrorState
          buttonLabel={i18n.t('sales_portal.error.refresh')}
          description={i18n.t('sales_portal.error.description')}
          onPress={refetch}
          title={i18n.t('sales_portal.error.title')}
        />
      );
    }

    const summaryCounts = getEmployeeSummary(employees);

    return (
      <>
        <Typography variant="heading-lg">
          {i18n.t('sales_portal.employee_list.title')}
        </Typography>
        <View style={styles.mainLayout}>
          <View style={styles.contentColumn}>
            {employees.length === 0 ? (
              <View style={styles.emptyStateWrapper}>
                <EmptyState
                  description={i18n.t(
                    'sales_portal.employee_list.empty_state.description',
                  )}
                  iconElement={emptyStateIcon}
                  title={i18n.t('sales_portal.employee_list.empty_state.title')}
                />
              </View>
            ) : (
              <>
                <View style={styles.searchBarWrapper}>
                  <SearchInput
                    onSearch={setSearchText}
                    placeholderText={i18n.t(
                      'sales_portal.employee_list.search_placeholder',
                    )}
                    searchText={searchText}
                  />
                </View>
                {filteredEmployees.length === 0 ? (
                  <View style={styles.emptyStateWrapper}>
                    <EmptyState
                      description={i18n.t(
                        'sales_portal.employee_list.no_results.description',
                      )}
                      iconElement={emptyStateIcon}
                      title={i18n.t(
                        'sales_portal.employee_list.no_results.title',
                        { searchText },
                      )}
                    />
                  </View>
                ) : (
                  filteredEmployees.map((employee) => (
                    <EmployeeListItem
                      employee={employee}
                      key={employee.employee_id}
                      onPress={openEditPanel}
                    />
                  ))
                )}
              </>
            )}
          </View>
          <View style={styles.sidebarColumn}>
            <EmployeeSummary
              counts={summaryCounts}
              onInvitePress={openInvitePanel}
            />
          </View>
        </View>
      </>
    );
  };

  const renderRightPanelContent = () => {
    if (!panelState) {
      return null;
    }
    if (panelState.kind === 'invite') {
      return (
        <EmployeeInvitePanelContent
          onClose={handlePanelClose}
          restaurantUUID={restaurantUUID}
          supplierUUID={supplierUUID}
        />
      );
    }
    if (notificationsError) {
      return (
        <SidePanel.Body
          accessibilityLabel={i18n.t('sales_portal.employee_edit_drawer.title')}
          role="dialog"
        >
          <ErrorState
            buttonLabel={i18n.t('sales_portal.error.refresh')}
            description={i18n.t('sales_portal.error.description')}
            icon={<NoInternetIcon stroke={theme.sk.color.icon.primary} />}
            onPress={() => refetchNotifications()}
            title={i18n.t('sales_portal.error.title')}
          />
        </SidePanel.Body>
      );
    }
    return (
      <EmployeeEditPanelContent
        employee={panelState.employee}
        // Adding a key forces a re-initilziation of this panel when our employee_id
        // changes so that we don't get stale data for our switches and fields
        key={panelState.employee.employee_id}
        notifications={notifications}
      />
    );
  };

  return (
    <SalesHubShellTemplate>
      <ContentWithPanelTemplate
        onClose={handlePanelClose}
        panelContent={
          panelState ? (
            <>
              <SidePanel.Header
                actions={
                  panelState.kind === 'edit' && !notificationsError ? (
                    <Button
                      accessibilityLabel={i18n.t(
                        'sales_portal.employee_edit_drawer.remove',
                      )}
                      disabled={removing}
                      onPress={onRemovePress}
                      size="$sm"
                      textProps={{ color: '$textError' }}
                      variant="tertiary"
                    >
                      {i18n.t('sales_portal.employee_edit_drawer.remove')}
                    </Button>
                  ) : null
                }
                leadingActions={
                  <IconButton
                    aria-label={i18n.t('sales_portal.side_panel.close')}
                    icon={<Icons.X color={theme.sk.color.icon.primary} />}
                    onPress={handlePanelClose}
                    shape="rounded"
                    size="$sm"
                    variant="tertiary"
                  />
                }
              />
              {renderRightPanelContent()}
            </>
          ) : undefined
        }
      >
        <AppPageLayout mode="focused" scroll title={pageTitle}>
          <View style={styles.body}>{renderBody()}</View>
        </AppPageLayout>
      </ContentWithPanelTemplate>
    </SalesHubShellTemplate>
  );
};

const useStyles = createStyles((theme) => ({
  body: {
    flex: 1,
    paddingTop: theme.sk.spacing.spaceLarge,
  },
  contentColumn: {
    flex: 3,
    paddingBottom: theme.sk.spacing.spaceMedium,
  },
  emptyStateWrapper: {
    marginTop: theme.sk.spacing.space2xlarge,
  },
  mainLayout: {
    flex: 1,
    flexDirection: 'row',
    gap: theme.sk.spacing.space3xlarge,
    marginTop: theme.sk.spacing.spaceMedium,
  },
  searchBarWrapper: {
    marginBottom: theme.sk.spacing.spaceMedium,
  },
  sidebarColumn: {
    flex: 1,
  },
}));
